import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * Request Builders for Manual Enrollment
 */

export interface EnrollmentData {
  firstName: string;
  lastName: string;
  registrationEmail: string;
  employeeInviteCode: string | null;
  password: string;
  phoneNumber: string | null;
  conditionsAccepted: (string | number)[];
  persistName?: boolean;
}

/**
 * Enroll a new employee
 */
export function enrollUserRequest(data: EnrollmentData): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'logins' },
    op: 'query',
    resourcePath: 'enroll',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url,
    method: 'POST',
    body: JSON.stringify(data),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Enroll a new family member
 */
export function enrollFamilyMemberRequest(data: EnrollmentData): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'logins' },
    op: 'query',
    resourcePath: 'enroll/enroll-family-member',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url,
    method: 'POST',
    body: JSON.stringify(data),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Demographics + signed-MPN payload for an LRAP enrollment submission.
 */
export interface LrapEnrollmentData {
  mpnSignatureDataUrl: string;
  demographics: {
    gender?: string | null;
    race?: string | null;
    ethnicity?: string | null;
    veteranStatus?: string | null;
    [key: string]: unknown;
  };
}

/**
 * Submit a completed LRAP enrollment (signed MPN + demographics) to
 * `POST /api/v2/lrap-enrollments`. The default namespace is empty
 * (`setBuildURLConfig({ namespace: '' })`), so the `api/v2` prefix is set
 * explicitly here.
 */
export function submitLrapEnrollmentRequest(data: LrapEnrollmentData): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'lrap-enrollments' },
    op: 'query',
    resourcePath: 'lrap-enrollments',
    namespace: 'api/v2',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url,
    method: 'POST',
    body: JSON.stringify(data),
    cacheOptions: { [SkipCache]: true },
  };
}
